import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://tvia0xs4d1.execute-api.us-east-1.amazonaws.com/Dev';
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://w8nam9nxgb.execute-api.us-east-1.amazonaws.com/Prod';

class SOWService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 60000, // 60 seconds timeout for SOW generation
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      async (config) => {
        try {
          // Get the current auth session
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('🔐 Added Authorization header to request');
          } else {
            console.warn('⚠️ No auth token available');
          }
        } catch (error) {
          console.warn('⚠️ Could not fetch auth session:', error.message);
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response.data,
      (error) => {
        const errorMessage = error.response?.data?.detail || error.message || 'An unknown error occurred';
        throw new Error(errorMessage);
      }
    );
  }

  async generateSOW(sowRequest) {
    try {
      console.group('🚀 SOW GENERATION PROCESS STARTED');
      console.log('📄 sowService.js: generateSOW() method called');
      console.log('📋 Request Details:', {
        customer_name: sowRequest.customer_name,
        project_type: sowRequest.project_type,
        scoping_notes_length: sowRequest.scoping_notes?.length || 0,
        project_title: sowRequest.project_title,
        estimated_duration: sowRequest.estimated_duration,
        budget_range: sowRequest.budget_range
      });
      console.log('🌐 API URL:', `${API_BASE_URL}/api/v1/sow/generate`);
      console.log('⏰ Starting API call to backend...');
      
      const startTime = performance.now();
      const response = await this.api.post('/api/v1/sow/generate', sowRequest);
      const endTime = performance.now();
      
      console.log('✅ Backend Response Received');
      console.log('⏱️  API Call Duration:', `${(endTime - startTime).toFixed(2)}ms`);
      console.log('📊 Response Status Code:', response.statusCode || 'N/A');
      
      // Handle async (202) responses - new async architecture
      if (response.job_id || response.document_id) {
        const jobId = response.job_id || response.document_id;
        
        // Check if this is an async response (has job_id and status indicates processing)
        if (response.status === 'queued' || response.status === 'processing' || 
            (response.job_id && !response.success)) {
          console.log('📊 Async Response - Generation queued for background processing');
          console.log('📄 Job ID:', jobId);
          console.log('⏳ Estimated completion:', response.estimated_completion || '30-60 seconds');
          console.groupEnd();
          
          return {
            success: true,
            job_id: jobId,
            status: response.status || 'processing',
            message: response.message || 'SOW generation started',
            estimated_completion: response.estimated_completion || '30-60 seconds',
            status_url: response.status_url || `/api/v1/sow/status/${jobId}`,
            created_at: response.created_at || new Date().toISOString(),
            async: true // Flag to indicate async processing
          };
        }
        
        // Handle sync completion (legacy or immediate completion)
        else if (response.status === 'completed' || response.success) {
          console.log('📊 Sync Response - Generation completed immediately');
          console.log('📄 Document ID:', response.document_id || response.job_id);
          console.log('⏱️  Generation Time:', response.generation_time);
          console.groupEnd();
          
          return {
            success: true,
            document_id: response.document_id || response.job_id,
            generation_time: response.generation_time,
            content: response.content,
            s3_key: response.s3_key,
            s3_url: response.s3_url || response.document_url,
            customer_name: response.customer_name,
            project_type: response.project_type,
            created_at: response.created_at,
            async: false // Flag to indicate sync processing
          };
        }
      }
      
      // Handle unknown response format
      else {
        console.log('📊 Unknown Response Format:', response);
        console.groupEnd();
        throw new Error('Unexpected response format from server');
      }
    } catch (error) {
      console.group('❌ SOW GENERATION ERROR');
      console.error('🔥 Error in sowService.generateSOW():', error);
      console.error('📋 Error Details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      console.groupEnd();
      
      // Enhanced error handling for async responses
      if (error.response?.status === 202) {
        // This shouldn't be an error - 202 is success for async
        const data = error.response.data;
        return {
          success: true,
          job_id: data.job_id,
          status: data.status,
          message: data.message,
          estimated_completion: data.estimated_completion,
          status_url: data.status_url,
          created_at: data.created_at,
          async: true
        };
      }
      
      throw error;
    }
  }

  async pollJobStatus(jobId, maxAttempts = 90, interval = 2000, onProgress = null) {
    console.log('🔍 Starting job status polling...');
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Don't wait on first attempt
        if (attempt > 1) {
          await new Promise(resolve => setTimeout(resolve, interval)); // eslint-disable-line no-loop-func
        }
        
        console.log(`📊 Polling attempt ${attempt}/${maxAttempts} for job: ${jobId}`);
        const statusResponse = await this.api.get(`/api/v1/sow/status/${jobId}`);
        
        console.log(`📈 Status: ${statusResponse.status} (${statusResponse.progress || 0}%)`);
        
        // Call progress callback if provided
        if (onProgress) {
          onProgress({
            status: statusResponse.status,
            progress: statusResponse.progress || 0,
            message: statusResponse.message,
            attempt: attempt,
            maxAttempts: maxAttempts,
            estimatedCompletion: statusResponse.estimated_completion
          });
        }
        
        if (statusResponse.status === 'completed') {
          console.log('✅ Job completed successfully!');
          return {
            success: true,
            job_id: jobId,
            document_id: jobId,
            generation_time: statusResponse.generation_time,
            s3_key: statusResponse.s3_key,
            s3_url: statusResponse.s3_url || statusResponse.document_url,
            download_url: statusResponse.download_url,
            filename: statusResponse.filename,
            file_size: statusResponse.file_size || statusResponse.size,
            created_at: statusResponse.created_at,
            completed_at: statusResponse.completed_at,
            customer_name: statusResponse.customer_name,
            project_type: statusResponse.project_type,
            async: true
          };
        } else if (statusResponse.status === 'failed') {
          console.error('❌ Job failed:', statusResponse.message);
          const errorDetails = statusResponse.error_details || {};
          throw new Error(`SOW generation failed: ${statusResponse.message}${errorDetails.error_type ? ` (${errorDetails.error_type})` : ''}`);
        }
        // Continue polling if status is 'processing', 'queued', or 'retrying'
        
      } catch (pollError) {
        console.warn(`⚠️ Polling attempt ${attempt} failed:`, pollError.message);
        
        // If it's a 404, the job might not exist
        if (pollError.response?.status === 404) {
          throw new Error(`Job ${jobId} not found. It may have been deleted or never existed.`);
        }
        
        // If we've reached max attempts, throw error
        if (attempt === maxAttempts) {
          throw new Error(`Job status polling failed after ${maxAttempts} attempts. Last error: ${pollError.message}`);
        }
        
        // For other errors, continue polling but with exponential backoff
        if (attempt > 10) {
          interval = Math.min(interval * 1.2, 10000); // Max 10 seconds
        }
      }
    }
    
    throw new Error(`Job did not complete within ${maxAttempts * interval / 1000} seconds`);
  }

  async downloadDocument(documentId, onProgress = null) {
    try {
      console.log(`📥 Starting download for document: ${documentId}`);
      
      // Create axios instance with progress tracking
      const downloadApi = axios.create({
        baseURL: API_BASE_URL,
        timeout: 120000, // 2 minutes for download
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: percentCompleted
            });
          }
        }
      });

      const response = await downloadApi.get(`/api/v1/sow/download/${documentId}`);

      // Create blob link to download with enhanced filename handling
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers with fallback
      let filename = documentId;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Ensure .docx extension
      if (!filename.toLowerCase().endsWith('.docx')) {
        filename += '.docx';
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log(`✅ Download completed: ${filename}`);
      return {
        success: true,
        filename: filename,
        size: response.data.size
      };
    } catch (error) {
      console.error('❌ Error downloading document:', error);
      
      // Enhanced error handling
      let errorMessage = 'Failed to download document';
      if (error.response?.status === 404) {
        errorMessage = 'Document not found. It may have been deleted or moved.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error occurred during download. Please try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Download timed out. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  async getDocumentMetadata(documentId) {
    try {
      console.log(`📋 Fetching metadata for document: ${documentId}`);
      const response = await this.api.get(`/documents/${documentId}/metadata`);
      return response;
    } catch (error) {
      console.error('Error fetching document metadata:', error);
      throw error;
    }
  }

  async listDocuments(filters = {}) {
    try {
      console.log('📂 Fetching document list with filters:', filters);
      const params = new URLSearchParams();
      
      if (filters.customerName) params.append('customer', filters.customerName);
      if (filters.status) params.append('status', filters.status);
      if (filters.maxDocuments) params.append('limit', filters.maxDocuments);
      
      const response = await this.api.get(`/api/v1/sow/jobs?${params.toString()}`);
      
      // Transform jobs to documents format
      const documents = (response.jobs || []).map(job => ({
        document_id: job.job_id,
        job_id: job.job_id,
        customer_name: job.customer_name,
        project_type: job.project_type,
        project_title: job.project_title,
        document_type: 'SOW',
        status: job.status,
        progress: job.progress,
        created_at: job.created_at,
        updated_at: job.updated_at,
        completed_at: job.completed_at,
        generation_time: job.generation_time,
        filename: job.filename,
        file_size: job.file_size,
        download_url: job.download_url,
        s3_key: job.s3_key,
        s3_location: job.s3_key ? 'S3' : null,
        storage_location: job.status === 'completed' ? 'S3' : 'temporary',
        error_type: job.error_type,
        error_message: job.error_message
      }));
      
      return {
        success: true,
        documents: documents,
        count: response.count,
        has_more: response.has_more,
        next_token: response.next_token
      };
    } catch (error) {
      console.error('Error listing documents:', error);
      throw error;
    }
  }

  async generateDownloadUrl(documentId, expirationHours = 1) {
    try {
      console.log(`🔗 Generating download URL for document: ${documentId}`);
      const response = await this.api.get(`/download-url/${documentId}?expiration_hours=${expirationHours}`);
      return response;
    } catch (error) {
      console.error('Error generating download URL:', error);
      throw error;
    }
  }

  async getJobStatus(jobId) {
    try {
      console.log(`📊 Fetching status for job: ${jobId}`);
      const response = await this.api.get(`/api/v1/sow/status/${jobId}`);
      return response;
    } catch (error) {
      console.error('Error fetching job status:', error);
      
      // Handle different error scenarios
      if (error.response?.status === 404) {
        throw new Error(`Job ${jobId} not found`);
      } else if (error.response?.status === 500) {
        // Backend error - might be transitioning to async architecture
        throw new Error(`Backend error checking job status. The async processing system may not be fully deployed yet.`);
      } else if (error.message.includes('Network Error')) {
        throw new Error(`Network error checking job status. Please check your connection.`);
      }
      
      throw new Error(`Failed to check job status: ${error.message}`);
    }
  }

  async downloadFromPresignedUrl(presignedUrl, filename = null) {
    try {
      console.log(`🔗 Downloading from presigned URL`);
      
      const response = await fetch(presignedUrl);
      
      if (!response.ok) {
        throw new Error(`Download failed with status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Use provided filename or extract from URL
      const downloadFilename = filename || presignedUrl.split('/').pop().split('?')[0] || 'document.docx';
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        filename: downloadFilename,
        size: blob.size
      };
    } catch (error) {
      console.error('Error downloading from presigned URL:', error);
      throw new Error(`Failed to download document: ${error.message}`);
    }
  }

  async downloadFromS3(s3Key, onProgress = null) {
    try {
      console.log(`☁️ Starting S3 download for: ${s3Key}`);
      
      // First, get the presigned URL from the backend
      const response = await this.api.get(`/download-from-s3/${s3Key}`);
      
      if (response.download_url) {
        // Use the presigned URL to download the file
        return await this.downloadFromPresignedUrl(response.download_url, s3Key.split('/').pop());
      } else {
        throw new Error('No download URL received from server');
      }
    } catch (error) {
      console.error('Error downloading from S3:', error);
      throw new Error(`Failed to download document from S3: ${error.message}`);
    }
  }

  async testBedrockConnection() {
    try {
      const response = await this.api.get('/test-bedrock');
      return response;
    } catch (error) {
      console.error('Error testing Bedrock connection:', error);
      throw error;
    }
  }

  async getUploadUrl(filename, contentType) {
    try {
      console.log(`🔗 Getting presigned upload URL for: ${filename}`);
      const response = await this.api.post('/api/v1/sow/upload-url', {
        filename,
        content_type: contentType,
      });
      return response; // { upload_url, s3_key, expires_in }
    } catch (error) {
      console.error('Error getting upload URL:', error);
      throw new Error(`Failed to get upload URL: ${error.message}`);
    }
  }

  async uploadFileToS3(uploadUrl, file) {
    try {
      console.log(`☁️ Uploading file to S3: ${file.name} (${file.size} bytes)`);
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!response.ok) {
        throw new Error(`S3 upload failed with status ${response.status}`);
      }
      console.log('✅ File uploaded to S3 successfully');
      return true;
    } catch (error) {
      console.error('Error uploading file to S3:', error);
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  }
}

export const sowService = new SOWService();